// tslint:disable:max-line-length
import classNames from "classnames";
import React from "react";

import "./Logo.scss";

interface Props {
  type?: "accent" | "light" | "dark";

  /**
   * Width (and height) of the logo in pixels.
   */
  width?: number;

  /**
   * Additional classname to use as well as .logo.
   */
  className?: string;

  /**
   * Additional styling for the container element.
   */
  style?: React.CSSProperties;
}

/**
 * TravelLocal's logo.
 *
 * @status stable
 */
export const Logo: React.FC<Props> = (props: Props) => {
  const { type = "accent", className, width, style } = props;
  const diameter = width || 125;

  return (
    <span
      className={classNames("logo", className)}
      style={{ ...style, ...(width ? { width, height: width } : {}) }}
    >
      <svg
        className={type}
        viewBox={`0 0 ${diameter} ${diameter}`}
        preserveAspectRatio="xMinYMin"
      >
        <g
          fill="none"
          fillRule="evenodd"
          transform={`scale(${diameter / 312} ${diameter / 312})`}
        >
          <title>TravelLocal logo</title>
          <path
            className="logo-path-a"
            fill="#007c80"
            d="M84.42 0H0v84.42A172.31 172.31 0 0 1 84.42 0ZM312 84.42V0h-84.42A172.31 172.31 0 0 1 312 84.42ZM227.58 312H312v-84.42A172.31 172.31 0 0 1 227.58 312ZM0 227.58V312h84.42A172.31 172.31 0 0 1 0 227.58ZM267.33 145.64c-2.54-.66-3-1-3-5.18V89.39a73.42 73.42 0 0 1 .75-10.84l-1.22-.94-18.86 1.7v3l3.86 1.79c2.55 1.23 3 1.61 3 5.75v50.6c0 4-.47 4.52-3.11 5.18l-3.3.85v3.3h25.16v-3.3ZM101.67 107.1c7.31 0 3.23 6.82 11.4 6.82 3.48 0 5.84-2.35 5.84-6.31 0-4.15-2.27-7.34-7.9-7.34-4.66 0-15.66.34-23.48 13.34h-.38l.57-12.72-1.13-.89L69 102.11v2.73l3.39 2c2.54 1.51 3 1.7 3 5.65v28c0 4-.47 4.52-3 5.18l-3.3.85v3.3h25.6v-3.3l-4-.85c-2.36-.38-2.83-.94-2.83-5.18V122c.67-3.14 7.5-14.9 13.81-14.9ZM221.33 99.94c-12.63 0-24.31 9.52-24.31 25.25s8 25.73 24.31 25.73c9.42 0 16.49-4.24 19.41-7.07v-4a31 31 0 0 1-14.7 3.77c-9.61 0-15.92-5.19-16.39-19.32H242c-.22-17.76-7.95-24.36-20.67-24.36Zm-11.59 19.88c.57-11.11 5.18-15.54 11-15.54 6.6 0 8.2 3.58 8.2 14.6ZM9.77 84.49l-.94 21.29h3.86c2.26-5.84 3.3-9 5.47-12.24C20.89 89.2 22.3 89 30.12 89h.29v50.41c0 5.18-.66 5.56-3.68 6.22l-4.05.75v3.4h29.21v-3.4l-4-.75c-3.21-.66-3.58-1-3.58-6.22V89h.18c7.92 0 9.24.1 12.06 4.53 2.08 3.2 3.21 6.4 5.38 12.24h3.86l-.99-21.28Z"
          />
          <path
            className="logo-path-b"
            fill="#007c80"
            d="M141 138.86v-26.39c0-13.14-16.16-12.66-18.57-12.53a.07.07 0 0 0 0 .14c1.21.59 6.11 3.43 6.11 11 0 7.07.05 10 .05 10l-9.13 2.07c-13.2 3.11-18.38 7.26-18.38 15.26s5.56 12.54 14.51 12.54c6 0 10.84-2.55 13.1-5.66h.47c.85 3.4 3.86 5.66 9 5.66a17.81 17.81 0 0 0 9.79-2.74v-3a10.17 10.17 0 0 1-4.52-.94c-2-.99-2.43-2.4-2.43-5.41ZM128.52 141c-1.13 1.89-3.76 3.49-7.53 3.49-4.62 0-7.63-1.89-7.63-7.63 0-7 3.1-9.42 9.32-10l5.84-.57ZM197 104.56v-3.3h-18v3.3l2.64.66c2 .47 2.64 1.51 2.64 3.2a15.44 15.44 0 0 1-1.13 5.18L175 137h-.37l-8.67-23.37a17.52 17.52 0 0 1-1.32-5c0-1.88.56-2.83 2.82-3.39l2.55-.66v-3.3h-25.19v3.3l2.07.66c2.74.85 3.49 2.17 5.47 6.88l15.45 37.69h7.26L189.39 113c2.07-5.37 2.82-7.16 5.37-7.82ZM68.48 224.13l3.68-.76c2.64-.56 3.48-1 3.48-6.22v-44.56c0-5.28-.84-5.75-3.48-6.32l-3.68-.75v-3.3h28.93v3.3l-4.43.85c-2.83.56-3.48.94-3.48 6.22V214c0 8.39.37 9 9.51 9 8.1 0 10.08-1.13 12.82-5.18a48.37 48.37 0 0 0 6.17-12.44h3.87l-1.42 22.14h-52ZM125.26 203.3c0-17.24 12.35-25.63 25.16-25.63 13.66 0 25 8.2 25 25.16 0 17.24-12.16 25.82-25 25.82-13.85 0-25.16-8.39-25.16-25.35Zm36.84.29c0-16.78-4.14-21.49-12.15-21.49-7.26 0-11.31 4.9-11.31 20.07 0 17 3.87 22.05 12.06 22.05 7.16 0 11.4-4.81 11.4-20.63ZM179.68 203.77c0-20 15.64-26.1 28.17-26.1 9.33 0 14.42 3.11 14.42 8.39 0 3.3-2.55 6.88-6.22 6.88-4.34 0-5.66-2.73-6.13-6.22-.56-4-1.41-4.52-4.61-4.52-6.6 0-12.91 4.05-12.91 17.71s5.37 21.39 16.11 21.39a28.54 28.54 0 0 0 13.94-3.86v4.05c-2.92 3-9.79 7.16-19.5 7.16-15.07 0-23.27-9.99-23.27-24.88ZM227.58 216.12c0-8 5.18-12.16 18.37-15.27l9.14-2.07V191c0-7.07-1.22-9.14-8-9.14-3.58 0-4.05.75-4.42 3.58-.48 4.9-2.17 7.44-6.88 7.44-4.05 0-5.75-2.16-5.75-5.37 0-6.31 8.95-9.8 19.69-9.8 11.69 0 17.81 3.87 17.81 12.54v26.38c0 3 .47 4.43 2.45 5.37a10 10 0 0 0 4.52.94v3a17.91 17.91 0 0 1-9.8 2.73c-5.18 0-8.19-2.26-9-5.65h-.47c-2.26 3.1-7.07 5.65-13.1 5.65-9-.02-14.56-4.54-14.56-12.55Zm27.51 2.64v-14.7l-5.84.56c-6.22.57-9.33 3-9.33 10 0 5.75 3 7.63 7.64 7.63 3.76-.01 6.44-1.61 7.53-3.49ZM277.28 224.22l3.3-.85c2.63-.66 3.1-1.22 3.1-5.18v-50.6c0-4.14-.47-4.52-3-5.74l-3.86-1.79v-3l18.84-1.7 1.23.95a72.25 72.25 0 0 0-.76 10.83v51.07c0 4.15.47 4.52 3 5.18l3.29.85v3.3h-25.14Z"
          />
        </g>
      </svg>
    </span>
  );
}
